import Container from "@mui/material/Container";
import { DataGrid } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataNotFound from "../../../components/DataNotFound.js";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import { useUser } from "../../../components/UserContext.js";
import { getPlots } from "../../../lib/apiConfig.js";
import { PAGE_LIST_CONTAINER_SX, ROUTES } from "../../../lib/constants.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";

const getEventsColumns = ({ locales }) => [
  {
    field: "eventName",
    headerName: locales["plot.event"],
    flex: 1,
  },
  {
    field: "eventTime",
    headerName: locales["plot.event.time.edit"],
    flex: 0.5,
  },
  {
    field: "eventLocation",
    headerName: locales["plot.event.location.edit"],
    flex: 1,
  },
  {
    field: "eventPlot",
    headerName: locales["plot.event.plotName"],
    flex: 1,
  },
];

const getEventsRows = ({ events }) =>
  events?.map(({ eventName, eventTime, eventLocation, id, name }) => ({
    id,
    eventName,
    eventTime,
    eventLocation,
    eventPlot: name,
  }));

export default function EventsList({ locales }) {
  const { runId } = useUser();
  const [plots, setPlots] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const navigate = useNavigate();

  const { isFetching } = useFetch({
    endpoint: getPlots({ runId }),
    mustFetch: !plots,
    onSuccess: setPlots,
    onFail: (error) => {
      setErrorMessage(error.message);
      setShowError(true);
    },
  });

  const plotsWithEvents = useMemo(
    () =>
      plots?.reduce(
        (acc, plot) => (plot?.eventName ? [...acc, plot] : acc),
        []
      ) || [],
    [plots]
  );

  const handleRowClick = ({ row }) => {
    navigate(
      `/${ROUTES.PLOTS.PLOTS.BASE}/${ROUTES.PLOTS.PLOTS.DETAIL.replace(
        ":id",
        row.id
      )}`,
      { state: { id: row.id } }
    );
  };

  return isFetching ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_LIST_CONTAINER_SX}>
      {plotsWithEvents.length ? (
        <DataGrid
          columns={getEventsColumns({ locales })}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnSelector
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 20 },
            },
          }}
          onRowClick={handleRowClick}
          pageSizeOptions={[20, 40]}
          rows={getEventsRows({ events: plotsWithEvents })}
          sx={(theme) => ({
            "--DataGrid-rowBorderColor": theme.palette.divider,
            "&.MuiDataGrid-root": {
              bgcolor: "background.paper",
              borderWidth: "0px",
            },
            "& .MuiDataGrid-columnHeader": {
              color: "text.secondary",
              fontSize: "medium",
              bgcolor: "background.paper",
              ":focus": {
                outline: "none",
              },
              ".MuiDataGrid-sortIcon": {
                color: "text.secondary",
              },
              ".MuiDataGrid-menuIconButton": {
                color: "text.secondary",
              },
            },
            "& .MuiDataGrid-iconSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-row": {
              color: "text.primary",
              fontSize: "medium",
              ":hover": {
                bgcolor: "background.default",
                cursor: "pointer",
              },
            },
            "& .MuiDataGrid-cell": {
              ":focus": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid var(--DataGrid-rowBorderColor)`,
            },
            "& .MuiTablePagination-selectIcon": {
              color: "text.primary",
            },
          })}
        />
      ) : (
        <DataNotFound text={locales["plots.events.notFound"]} />
      )}
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
